/**
 * FilesPickerModal - pick documents that already live in Files (F-DMS).
 *
 * Every uploader used to know only "Upload from this computer", so a document
 * already in Files had to be downloaded and uploaded again. This modal is the
 * one place a user picks EXISTING documents:
 *
 *   Files                   breadcrumbed folder browsing, search, multi-select
 *   Google Drive            the Google Picker → server-side import into Files
 *   OneDrive / SharePoint   the OneDrive picker → server-side import into Files
 *
 * Whatever the source, it resolves with Files (`PickedFile[]`): an external
 * pick is imported by `POST /integrations/<provider>/import` first, so a
 * consumer always receives Files uuids. Open it through `useFilesPicker()`.
 */

import logger from '../../../utils/logger';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCaretRightLight,
  PiFolderOpenLight,
  PiFolderSimpleLight,
  PiGoogleDriveLogoLight,
  PiWarningCircleLight,
  PiWindowsLogoLight,
} from 'react-icons/pi';
import ModalShell from '../ModalShell';
import Button from '../TailwindComponents/Button';
import Checkbox from '../TailwindComponents/CheckBox';
import EmptyState from '../TailwindComponents/EmptyState';
import SearchInput from '../TailwindComponents/SearchInput';
import Skeleton from '../TailwindComponents/Skeleton';
import Spinner from '../TailwindComponents/Spinner';
import SubTabBar, { SubTab } from '../TailwindComponents/SubTabBar';
import { FileGlyph, FolderGlyph } from '../../Files/fileGlyphs';
import { formatFileSize } from '../attachments/attachmentModel';
import { fetchFolderContents, fetchRootFiles } from '../../../utils/FileApiBuilder';
import { useGoogleDrive } from '../../../contexts/GoogleContext';
import { useMicrosoftGraph } from '../../../contexts/MicrosoftGraphContext';
import {
  ExternalImportError,
  fetchIntegrationStatus,
  importExternalItems,
  IntegrationNotConnectedError,
  IntegrationStatus,
} from './externalImport';
import {
  ExternalProvider,
  FilesPickerOptions,
  INTEGRATIONS_SETTINGS_PATH,
  isAccepted,
  PickedFile,
  PickedFileSource,
} from './filesPickerTypes';

export interface FilesPickerModalProps {
  isOpen: boolean;
  options?: FilesPickerOptions;
  onCancel: () => void;
  onConfirm: (files: PickedFile[]) => void;
}

interface Crumb {
  id: string;
  name: string;
}

interface Row {
  id: string;
  name: string;
  isFolder: boolean;
  size?: number;
  mime?: string;
  folderId?: string;
}

const PAGE_SIZE = 100;
const ALL_SOURCES: PickedFileSource[] = ['files', 'gdrive', 'onedrive'];

const prettyFolderName = (name: string) => (name || '').replace(/_/g, ' ');

/** Backend item → a row. Folders have a FolderName and no FileID. */
const toRow = (item: any, currentFolderId: string): Row | null => {
  if (!item) return null;
  if (!item.FileID && item.FolderName) {
    return { id: item.FolderID, name: prettyFolderName(item.FolderName), isFolder: true };
  }
  if (!item.FileID) return null;
  return {
    id: item.FileID,
    name: item.FileName || '',
    isFolder: false,
    size: typeof item.FileSize === 'number' ? item.FileSize : undefined,
    mime: item.FileType || undefined,
    folderId: item.FolderID ?? item.LocationFolderID ?? currentFolderId,
  };
};

const FilesPickerModal: React.FC<FilesPickerModalProps> = ({ isOpen, options, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const google = useGoogleDrive();
  const microsoft = useMicrosoftGraph();

  const multiple = options?.multiple !== false;
  const accept = options?.accept;
  const maxFiles = options?.maxFiles;
  const sources = (options?.sources && options.sources.length ? options.sources : ALL_SOURCES).filter((s) =>
    ALL_SOURCES.includes(s),
  );

  const [source, setSource] = useState<PickedFileSource>(options?.initialSource || sources[0] || 'files');

  // ── Files browsing ────────────────────────────────────────────────────────
  const [path, setPath] = useState<Crumb[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState<Map<string, PickedFile>>(new Map());

  // ── External sources ──────────────────────────────────────────────────────
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [importing, setImporting] = useState<ExternalProvider | null>(null);
  const [importErrors, setImportErrors] = useState<ExternalImportError[]>([]);
  const [externalError, setExternalError] = useState<string | null>(null);

  // Fresh state every time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setSource(options?.initialSource || sources[0] || 'files');
    setPath([]);
    setSearch('');
    setDebouncedSearch('');
    setSelected(new Map());
    setImportErrors([]);
    setExternalError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const currentFolderId = path.length ? path[path.length - 1].id : '';
  const parentFolderId = path.length > 1 ? path[path.length - 2].id : undefined;

  const requestId = useRef(0);
  const load = useCallback(
    async (pageToLoad: number) => {
      const id = ++requestId.current;
      if (pageToLoad === 1) setRows(null);
      else setLoadingMore(true);
      setLoadError(false);
      try {
        const query = { limit: PAGE_SIZE, searchTerm: debouncedSearch };
        const res = currentFolderId
          ? await fetchFolderContents(currentFolderId, pageToLoad, parentFolderId, query)
          : await fetchRootFiles({ ...query, page: pageToLoad });
        if (id !== requestId.current) return;
        const folders = res?.folders || res?.subfolders || [];
        const files = res?.files || [];
        const items: any[] = res?.items || [...folders, ...files];
        const next = items.map((item) => toRow(item, currentFolderId)).filter((r): r is Row => !!r);
        setRows((prev) => (pageToLoad === 1 || !prev ? next : [...prev, ...next]));
        setPage(pageToLoad);
        setHasNext(!!res?.pagination?.has_next);
      } catch {
        if (id !== requestId.current) return;
        setLoadError(true);
        setRows((prev) => (pageToLoad === 1 ? [] : prev));
      } finally {
        if (id === requestId.current) setLoadingMore(false);
      }
    },
    [currentFolderId, parentFolderId, debouncedSearch],
  );

  useEffect(() => {
    if (isOpen && source === 'files') load(1);
  }, [isOpen, source, load]);

  // Integration status, re-read when the user comes back from connecting in another tab.
  useEffect(() => {
    if (!isOpen || !sources.some((s) => s !== 'files')) return;
    let alive = true;
    const read = () => fetchIntegrationStatus().then((s) => alive && setStatus(s));
    read();
    window.addEventListener('focus', read);
    return () => {
      alive = false;
      window.removeEventListener('focus', read);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // The backend searches the whole tree; the name filter also narrows whatever is on screen.
  const visibleRows = useMemo(() => {
    if (!rows) return null;
    const needle = debouncedSearch.toLowerCase();
    const filtered = needle ? rows.filter((r) => r.name.toLowerCase().includes(needle)) : rows;
    return [...filtered].sort((a, b) => Number(b.isFolder) - Number(a.isFolder));
  }, [rows, debouncedSearch]);

  const limitReached = (count: number) => (maxFiles ? count >= maxFiles : false);

  const toggle = (row: Row) => {
    setSelected((prev) => {
      const next = new Map(multiple ? prev : []);
      if (prev.has(row.id)) {
        next.delete(row.id);
        return next;
      }
      if (multiple && limitReached(prev.size)) return prev;
      next.set(row.id, {
        uuid: row.id,
        name: row.name,
        size: row.size,
        mime_type: row.mime,
        source: 'files',
        folder_uuid: row.folderId || '',
      });
      return next;
    });
  };

  const openFolder = (row: Row) => {
    setSearch('');
    setDebouncedSearch('');
    setPath((prev) => [...prev, { id: row.id, name: row.name }]);
  };

  const goTo = (index: number) => {
    setSearch('');
    setDebouncedSearch('');
    setPath((prev) => (index < 0 ? [] : prev.slice(0, index + 1)));
  };

  const confirm = () => {
    const files = Array.from(selected.values());
    if (files.length) onConfirm(files);
  };

  // ── External pick + import ────────────────────────────────────────────────
  const markNotConnected = (provider: ExternalProvider) =>
    setStatus((prev) => ({ ...(prev || { gdrive: false, onedrive: false }), [provider]: false }));

  const pickExternal = async (provider: ExternalProvider) => {
    setImportErrors([]);
    setExternalError(null);
    // Which step failed decides the message: the provider's picker (nothing
    // reached PONS) or the import into Files.
    let stage: 'pick' | 'import' = 'pick';
    try {
      const pick = provider === 'gdrive' ? google.pickItems : microsoft.pickItems;
      const items = await pick({ accept, multiple });
      if (!items || !items.length) return;
      stage = 'import';
      setImporting(provider);
      const result = await importExternalItems(provider, items, options?.importFolderUuid);
      const imported = multiple ? result.files : result.files.slice(0, 1);
      if (!result.errors.length && imported.length) {
        onConfirm([...Array.from(selected.values()), ...imported]);
        return;
      }
      // Partial import: keep what arrived selected and say what did not.
      setImportErrors(result.errors);
      if (imported.length) {
        setSelected((prev) => {
          const next = new Map(multiple ? prev : []);
          imported.forEach((f) => next.set(f.uuid, f));
          return next;
        });
      }
    } catch (error) {
      if (error instanceof IntegrationNotConnectedError) {
        markNotConnected(provider);
      } else if (stage === 'pick') {
        logger.warn('External picker failed', { provider, error: String((error as any)?.message || (error as any)?.errorCode || error) });
        setExternalError(t('The {{name}} picker could not open. Try reconnecting it in Settings › Integrations.', {
          name: provider === 'gdrive' ? t('Google Drive') : t('OneDrive / SharePoint'),
        }));
      } else {
        setExternalError(t('Could not import the selected documents. Please try again.'));
      }
    } finally {
      setImporting(null);
    }
  };

  // ── Rendering ─────────────────────────────────────────────────────────────
  const tabs: SubTab[] = sources.map((s) =>
    s === 'files'
      ? { id: 'files', label: t('Files'), icon: <PiFolderSimpleLight className="h-4 w-4" /> }
      : s === 'gdrive'
        ? { id: 'gdrive', label: t('Google Drive'), icon: <PiGoogleDriveLogoLight className="h-4 w-4" /> }
        : { id: 'onedrive', label: t('OneDrive / SharePoint'), icon: <PiWindowsLogoLight className="h-4 w-4" /> },
  );

  const selectedCount = selected.size;

  const renderFiles = () => (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <nav aria-label={t('Folder path')} className="flex flex-wrap items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => goTo(-1)}
          className={clsx(
            'rounded px-1 transition-colors hover:text-fg-strong',
            path.length ? 'text-fg-muted' : 'text-fg-strong',
          )}
          aria-current={path.length ? undefined : 'page'}
        >
          {t('Files')}
        </button>
        {path.map((crumb, index) => (
          <React.Fragment key={crumb.id}>
            <PiCaretRightLight className="h-3 w-3 text-fg-muted" aria-hidden />
            <button
              type="button"
              onClick={() => goTo(index)}
              className={clsx(
                'max-w-[12rem] truncate rounded px-1 transition-colors hover:text-fg-strong',
                index === path.length - 1 ? 'text-fg-strong' : 'text-fg-muted',
              )}
              aria-current={index === path.length - 1 ? 'page' : undefined}
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </nav>

      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder={t('Search files')}
        aria-label={t('Search files')}
        size="small"
      />

      <div className="min-h-[16rem] flex-1 overflow-y-auto custom-scrollbar-main" data-testid="files-picker-list">
        {visibleRows === null ? (
          <Skeleton variant="list" rows={6} />
        ) : loadError && visibleRows.length === 0 ? (
          <EmptyState
            density="compact"
            icon={<PiWarningCircleLight />}
            headline={t('Could not load your files')}
            button={{ label: t('Try again'), onClick: () => load(1) }}
          />
        ) : visibleRows.length === 0 ? (
          <EmptyState
            density="compact"
            icon={<PiFolderOpenLight />}
            headline={debouncedSearch ? t('No files match your search') : t('This folder is empty')}
          />
        ) : (
          <ul className="space-y-1">
            {visibleRows.map((row) =>
              row.isFolder ? (
                <li key={`folder-${row.id}`}>
                  <button
                    type="button"
                    onClick={() => openFolder(row)}
                    className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-start text-sm text-fg-body transition-colors hover:bg-surface-sunken/70"
                    aria-label={t('Open folder {{name}}', { name: row.name })}
                  >
                    <FolderGlyph folderName={row.name} className="h-5 w-5 flex-shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{row.name}</span>
                    <PiCaretRightLight className="h-4 w-4 flex-shrink-0 text-fg-muted" aria-hidden />
                  </button>
                </li>
              ) : (
                <FileRow
                  key={`file-${row.id}`}
                  row={row}
                  checked={selected.has(row.id)}
                  disabled={
                    !isAccepted(row.name, accept) ||
                    (!selected.has(row.id) && multiple && limitReached(selectedCount))
                  }
                  unsupported={!isAccepted(row.name, accept)}
                  onToggle={() => toggle(row)}
                />
              ),
            )}
          </ul>
        )}
        {hasNext && visibleRows && visibleRows.length > 0 && (
          <div className="flex justify-center py-2">
            <Button variant="outline" size="small" isLoading={loadingMore} onClick={() => load(page + 1)}>
              {t('Load more')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const renderExternal = (provider: ExternalProvider) => {
    const name = provider === 'gdrive' ? t('Google Drive') : t('OneDrive / SharePoint');
    const icon =
      provider === 'gdrive' ? <PiGoogleDriveLogoLight /> : <PiWindowsLogoLight />;
    if (status === null) {
      return (
        <div className="flex min-h-[16rem] items-center justify-center">
          <Spinner size="md" label={t('Checking connection')} />
        </div>
      );
    }
    if (!status[provider]) {
      return (
        <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3 text-center" data-testid="not-connected">
          <span className="text-fg-muted [&>svg]:h-10 [&>svg]:w-10">{icon}</span>
          <p className="text-sm text-fg-strong">{t('{{name}} is not connected', { name })}</p>
          <p className="max-w-sm text-sm text-fg-muted">
            {t('Connect it once in Settings, then pick documents from it here.')}
          </p>
          <a
            href={INTEGRATIONS_SETTINGS_PATH}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-fg-strong underline decoration-champagne underline-offset-4 hover:text-champagne"
          >
            {t('Connect in Settings')}
          </a>
        </div>
      );
    }
    return (
      <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3 text-center">
        <span className="text-fg-muted [&>svg]:h-10 [&>svg]:w-10">{icon}</span>
        <p className="max-w-sm text-sm text-fg-muted">
          {t('Pick documents from {{name}}. They are copied into Files so they can be used here.', { name })}
        </p>
        <Button
          variant="primary"
          size="small"
          isLoading={importing === provider}
          disabled={!!importing}
          onClick={() => pickExternal(provider)}
        >
          {importing === provider ? t('Importing…') : t('Choose from {{name}}', { name })}
        </Button>
        {externalError && (
          <p role="alert" className="text-xs text-danger-600 dark:text-danger-400">
            {externalError}
          </p>
        )}
        {importErrors.length > 0 && (
          <div role="alert" className="w-full max-w-md rounded-md border border-hairline-soft bg-surface-sunken/70 px-3 py-2 text-start">
            <p className="text-xs text-fg-strong">{t('Some documents could not be imported')}</p>
            <ul className="mt-1 space-y-0.5">
              {importErrors.map((e) => (
                <li key={e.id} className="text-xs text-fg-muted break-words">
                  {(e.name || e.id) + ': ' + e.error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onCancel}
      title={options?.title || t('Select from Files')}
      description={
        accept && accept.length
          ? t('Allowed types: {{types}}', { types: accept.map((a) => a.toUpperCase()).join(', ') })
          : undefined
      }
      icon={<PiFolderSimpleLight />}
      size="lg"
      height="tall"
      bodyClassName="flex min-h-0 flex-col gap-3 overflow-hidden px-6 py-4"
      data-testid="files-picker"
      // Opened FROM uploaders that often sit inside GlobalModal
      // (z-[300000000]) or the side panel - it must stack above both.
      zIndexClassName="z-[300000002]"
      footer={
        <>
          <span className="me-auto text-sm text-fg-muted" aria-live="polite">
            {t('{{n}} selected', { n: selectedCount })}
          </span>
          <Button variant="outline" size="small" onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" size="small" disabled={selectedCount === 0 || !!importing} onClick={confirm}>
            {t('Use selected')}
          </Button>
        </>
      }
    >
      {tabs.length > 1 && (
        <SubTabBar tabs={tabs} activeTab={source} onTabChange={(id) => setSource(id as PickedFileSource)} />
      )}
      {source === 'files' ? renderFiles() : renderExternal(source)}
    </ModalShell>
  );
};

const FileRow: React.FC<{
  row: Row;
  checked: boolean;
  disabled: boolean;
  unsupported: boolean;
  onToggle: () => void;
}> = ({ row, checked, disabled, unsupported, onToggle }) => {
  const { t } = useTranslation();
  const meta = [row.size !== undefined ? formatFileSize(row.size) : '', unsupported ? t('Not supported here') : '']
    .filter(Boolean)
    .join(' · ');
  return (
    <li
      className={clsx(
        'flex items-center gap-2 rounded-md px-2.5 py-2 transition-colors',
        checked ? 'bg-champagne/10' : 'hover:bg-surface-sunken/70',
      )}
    >
      <FileGlyph fileName={row.name} mimeType={row.mime} className="h-5 w-5 flex-shrink-0" />
      <Checkbox
        label={row.name}
        translate={false}
        description={meta || undefined}
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        className="min-w-0 flex-1"
      />
    </li>
  );
};

export default FilesPickerModal;
